/**
 * Urlaub & Abwesenheit – Sammelseite mit rollenbasierten Tabs:
 *   1. Mein Urlaub  – eigene Anträge, Drag&Drop-Kalender (alle)
 *   2. Team-Kalender – Gantt-Übersicht (Manager/Admin mit vacations:view)
 *   3. Anträge       – Genehmigungs-Queue mit Namen (vacations:execute)
 *
 * Der aktive Tab wird in der URL als ?tab=… persistiert, damit Direkt-
 * Links und Browser-Back funktionieren. Alte Routen (/vacations,
 * /team-kalender, /vacation-requests) leiten auf diese Seite um.
 */
import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Palmtree, Users, ClipboardList, Building2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Vacations from './Vacations';
import TeamCalendar from './TeamCalendar';
import VacationRequests from './VacationRequests';
import VerwaltungPresence from './team-calendar/VerwaltungPresence';
import { vacationsAPI } from '../lib/api';
import { useEffect, useState } from 'react';

const TAB_KEYS = {
  MINE: 'mine',
  TEAM: 'team',
  REQUESTS: 'requests',
  VERWALTUNG: 'verwaltung',
};

export default function UrlaubHub() {
  const { hasPermission } = useAuth();
  const canViewTeam = hasPermission('vacations', 'view');
  const canApprove = hasPermission('vacations', 'execute');

  const [searchParams, setSearchParams] = useSearchParams();
  const [pendingCount, setPendingCount] = useState(0);

  const availableTabs = useMemo(() => {
    const tabs = [{ key: TAB_KEYS.MINE, label: 'Mein Urlaub', icon: Palmtree, testid: 'tab-mine' }];
    if (canViewTeam) tabs.push({ key: TAB_KEYS.TEAM, label: 'Team-Kalender', icon: Users, testid: 'tab-team' });
    if (canViewTeam) tabs.push({ key: TAB_KEYS.VERWALTUNG, label: 'Verwaltung – Präsenz', icon: Building2, testid: 'tab-verwaltung' });
    // Antraege-Tab fuer ALLE sichtbar (Mitarbeiter sehen ihre eigenen Antraege,
    // Manager sehen zusaetzlich die Genehmigungs-Queue)
    tabs.push({ key: TAB_KEYS.REQUESTS, label: 'Anträge', icon: ClipboardList, testid: 'tab-requests' });
    return tabs;
  }, [canViewTeam]);

  const activeTab = useMemo(() => {
    const requested = searchParams.get('tab');
    if (requested && availableTabs.some((t) => t.key === requested)) return requested;
    // Default: Manager mit pending-Anträgen → direkt zur Genehmigungs-Queue
    if (canApprove && pendingCount > 0) return TAB_KEYS.REQUESTS;
    return TAB_KEYS.MINE;
  }, [searchParams, availableTabs, canApprove, pendingCount]);

  const handleTabChange = (val) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', val);
    setSearchParams(next, { replace: true });
  };

  // Pending-Anträge zählen für den Badge im Tab "Anträge"
  useEffect(() => {
    if (!canApprove) return;
    let cancelled = false;
    vacationsAPI.getPending()
      .then((res) => { if (!cancelled) setPendingCount((res.data || []).length); })
      .catch(() => { if (!cancelled) setPendingCount(0); });
    return () => { cancelled = true; };
  }, [canApprove]);

  return (
    <DashboardLayout>
      <div className="space-y-4 animate-fade-in" data-testid="urlaub-hub-page">
        <div>
          <h1 className="font-heading font-bold text-xl sm:text-3xl text-foreground">Urlaub & Abwesenheit</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Eigene Anträge, Team-Übersicht und Genehmigungen — alles auf einer Seite.
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="grid grid-cols-2 gap-1 h-auto w-full sm:flex sm:h-9 sm:w-auto">
            {availableTabs.map((t) => (
              <TabsTrigger
                key={t.key}
                value={t.key}
                data-testid={t.testid}
                className="flex items-center justify-center gap-2 whitespace-normal text-center leading-tight py-1.5 text-xs sm:text-sm sm:whitespace-nowrap"
              >
                <t.icon className="h-4 w-4 shrink-0" />
                <span>{t.label}</span>
                {t.key === TAB_KEYS.REQUESTS && pendingCount > 0 && (
                  <Badge variant="destructive" className="ml-1 px-1.5 py-0 text-[10px]" data-testid="pending-badge">
                    {pendingCount}
                  </Badge>
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value={TAB_KEYS.MINE} className="mt-4">
            <Vacations embedded />
          </TabsContent>

          {canViewTeam && (
            <TabsContent value={TAB_KEYS.TEAM} className="mt-4">
              <TeamCalendar embedded />
            </TabsContent>
          )}

          {canViewTeam && (
            <TabsContent value={TAB_KEYS.VERWALTUNG} className="mt-4">
              <VerwaltungPresence />
            </TabsContent>
          )}

          <TabsContent value={TAB_KEYS.REQUESTS} className="mt-4">
            <VacationRequests embedded />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
